/**
 * Asset readiness (q-asset-readiness-loading-screen, AD-794). The client used to show
 * the world the moment it could draw anything: placeholder "pill" bodies, grey dressing
 * boxes and props popping in one by one. The loading screen now holds the picture until
 * what the camera can SEE has loaded, then reveals it.
 *
 * Three parts, all client-local and presentation-only (nothing on the wire):
 *
 *   - {@link PendingAssets} — a registry the loaders report their in-flight loads to, each
 *     with an ANCHOR saying where in the world the load will appear (a point, a box, a live
 *     object, or "everywhere" for the ground texture).
 *   - {@link evaluateReadiness} — the predicate. Ready means: the world descriptor applied,
 *     the content pack settled, the own avatar drawn with its real mesh, no placeholder
 *     body (capsule / cone) inside the view, and no pending load anchored inside the view.
 *     "The view" is the camera frustum clipped to the draw range (the far plane or the fog,
 *     whichever is nearer). A load OUTSIDE the view never holds the screen: this is what
 *     keeps the low tier's FirstFrameGate saving (enemy rigs out of shot keep streaming
 *     after the reveal; scene/firstFrameGate.ts).
 *   - {@link ReadinessSession} — the latch. It goes ready once the predicate has held for
 *     {@link READY_HOLD_MS} and at least {@link READY_HOLD_FRAMES} samples, or at
 *     {@link READINESS_TIMEOUT_MS} regardless (logging what was still pending), and it
 *     reports honest progress: how many of the items it has seen pending are done.
 *
 * A permanent fallback (a creature def with no model, a rig pack that never loads) is NOT
 * pending — waiting would not change it. Those are excluded from the census by the caller
 * and listed in the result as `fallbacks`, so a pill on screen is still reported.
 */

import * as THREE from 'three';

import type { PendingAnchor } from './pendingAssets.js';
import { PLACEHOLDER_BODY_KEY, type PlaceholderKind } from './placeholderBodies.js';

export {
  GLOBAL_ANCHOR,
  PendingAssets,
  objectAnchor,
  pointsAnchor,
  type PendingAnchor,
  type PendingAssetSink,
  type PendingEntry,
  type Vec3Like,
} from './pendingAssets.js';

/** Lift the loading screen after this long whatever is still pending (ms from boot). */
export const READINESS_TIMEOUT_MS = 30_000;
/**
 * The predicate must hold this long before the reveal (ms): a load that settles and at
 * once starts the next one (rig, then the equipment on it) must not reveal a half-built frame.
 */
export const READY_HOLD_MS = 300;
/** …and over at least this many samples (each sample follows a rendered frame). */
export const READY_HOLD_FRAMES = 3;
/** How often the Game samples the predicate while the screen is up (ms). */
export const READINESS_SAMPLE_MS = 100;

/** The volume a reveal shows: the camera frustum, clipped to the draw range. */
export interface ViewVolume {
  readonly frustum: THREE.Frustum;
  readonly eye: THREE.Vector3;
  readonly range: number;
}

/** The view volume of `camera` out to `range` metres (the far plane or fog, nearer wins). */
export function viewVolume(camera: THREE.Camera, range: number): ViewVolume {
  camera.updateMatrixWorld();
  const frustum = new THREE.Frustum().setFromProjectionMatrix(
    new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse),
  );
  return { frustum, eye: camera.getWorldPosition(new THREE.Vector3()), range };
}

/** The draw range for a camera and fog: the nearer of the far plane and the fog's far edge. */
export function drawRange(camera: THREE.Camera, fog: THREE.Scene['fog']): number {
  const far = camera instanceof THREE.PerspectiveCamera ? camera.far : Infinity;
  const fogFar = fog instanceof THREE.Fog ? fog.far : Infinity;
  return Math.min(far, fogFar);
}

/** Whether `object` is attached to a scene with no hidden ancestor (i.e. it can be drawn). */
export function drawable(object: THREE.Object3D): boolean {
  let node: THREE.Object3D | null = object;
  while (node !== null) {
    if (!node.visible) return false;
    if ((node as THREE.Scene).isScene === true) return true;
    node = node.parent;
  }
  return false;
}

const scratchBox = new THREE.Box3();
const scratchSphere = new THREE.Sphere();

/** Whether a pending load's anchor lies inside the view volume. */
export function anchorInView(anchor: PendingAnchor, view: ViewVolume): boolean {
  if (anchor.kind === 'global') return true;
  if (anchor.kind === 'box') {
    scratchBox.min.set(anchor.min.x, anchor.min.y, anchor.min.z);
    scratchBox.max.set(anchor.max.x, anchor.max.y, anchor.max.z);
    return (
      view.frustum.intersectsBox(scratchBox) && scratchBox.distanceToPoint(view.eye) <= view.range
    );
  }
  if (!drawable(anchor.object)) return false;
  anchor.object.updateWorldMatrix(true, false);
  anchor.object.getWorldPosition(scratchSphere.center);
  scratchSphere.radius = anchor.radius;
  return (
    view.frustum.intersectsSphere(scratchSphere) &&
    scratchSphere.center.distanceTo(view.eye) - anchor.radius <= view.range
  );
}

/** Radius (m) of a placeholder body for the view test (a capsule is ~1.7 m tall). */
const BODY_RADIUS = 1.2;
/** Height (m) of a body's centre above its group origin. */
const BODY_CENTRE_Y = 0.85;

/** A placeholder body (capsule / cone) the view would show. */
export interface BodyInView {
  readonly kind: 'own' | PlaceholderKind;
  readonly group: THREE.Object3D;
}

/**
 * The placeholder bodies inside the view, one per entity group, skipping groups in
 * `exclude` (permanent fallbacks). The own avatar's capsule counts wherever it is: the
 * camera follows it, and an own pill is never acceptable at the reveal.
 */
export function placeholderBodiesInView(
  root: THREE.Object3D,
  view: ViewVolume,
  own: THREE.Object3D | null,
  exclude: ReadonlySet<THREE.Object3D> = new Set(),
): BodyInView[] {
  const groups = new Map<THREE.Object3D, PlaceholderKind>();
  root.traverseVisible((obj) => {
    const kind = obj.userData[PLACEHOLDER_BODY_KEY] as PlaceholderKind | undefined;
    if (kind !== undefined) groups.set(obj.parent ?? obj, kind);
  });
  const out: BodyInView[] = [];
  for (const [group, kind] of groups) {
    if (exclude.has(group)) continue;
    if (group === own) {
      out.push({ kind: 'own', group });
      continue;
    }
    group.updateWorldMatrix(true, false);
    group.getWorldPosition(scratchSphere.center);
    scratchSphere.center.y += BODY_CENTRE_Y;
    scratchSphere.radius = BODY_RADIUS;
    if (
      view.frustum.intersectsSphere(scratchSphere) &&
      scratchSphere.center.distanceTo(view.eye) - BODY_RADIUS <= view.range
    ) {
      out.push({ kind, group });
    }
  }
  return out;
}

/** The content pack's state as the readiness predicate sees it. */
export type ContentPackState = 'pending' | 'loaded' | 'unavailable';

/** Everything the predicate reads, gathered by the Game once per sample. */
export interface ReadinessInputs {
  /** The join descriptor arrived and the world scene was applied. */
  readonly worldApplied: boolean;
  /** `unavailable` (no pack, or its sync failed) is terminal: it does not hold the screen. */
  readonly contentPack: ContentPackState;
  /** The own avatar exists (the server named our entity and the group was built). */
  readonly ownAvatar: boolean;
  /** Placeholder bodies inside the view (own capsule included), already minus fallbacks. */
  readonly bodiesInView: readonly { readonly key: string; readonly label: string }[];
  /** Pending loads anchored inside the view. */
  readonly loadsInView: readonly { readonly key: string; readonly label: string }[];
}

/** One thing the screen is waiting on: a stable key (for progress) and a readable label. */
export interface WaitItem {
  readonly key: string;
  readonly label: string;
}

/** The predicate's answer for one sample. */
export interface ReadinessVerdict {
  readonly ready: boolean;
  readonly waitingOn: readonly WaitItem[];
}

/** The predicate: ready when nothing in {@link ReadinessInputs} is still outstanding. */
export function evaluateReadiness(inputs: ReadinessInputs): ReadinessVerdict {
  const waitingOn: WaitItem[] = [];
  if (!inputs.worldApplied) waitingOn.push({ key: 'world', label: 'the world' });
  if (inputs.contentPack === 'pending')
    waitingOn.push({ key: 'content', label: 'the content pack' });
  if (!inputs.ownAvatar) waitingOn.push({ key: 'avatar', label: 'your character' });
  waitingOn.push(...inputs.bodiesInView, ...inputs.loadsInView);
  return { ready: waitingOn.length === 0, waitingOn };
}

/** Why the screen lifted. */
export type ReadinessReason = 'ready' | 'timeout';

/** Honest progress: items seen pending so far, and how many of them are done. */
export interface ReadinessProgress {
  readonly done: number;
  readonly total: number;
}

/** The session's published state (`window.__tgcReadyInfo`, the overlay, the scene dump). */
export interface ReadinessState {
  readonly ready: boolean;
  readonly reason: ReadinessReason | null;
  /** ms since the session started (the loading screen appeared). */
  readonly elapsedMs: number;
  /** `performance.now()` at the latch, or null while loading. */
  readonly readyAtMs: number | null;
  readonly progress: ReadinessProgress;
  /** Labels still pending — the live list while loading, the leftovers at a timeout. */
  readonly waitingOn: readonly string[];
  /** Permanent fallbacks on screen at the latch (reported, never waited on). */
  readonly fallbacks: readonly string[];
}

export interface ReadinessSessionOptions {
  /** `performance.now()` when the loading screen appeared. */
  startMs: number;
  timeoutMs?: number;
  holdMs?: number;
  holdFrames?: number;
  /** Log sink for the latch (default console). */
  log?: (level: 'info' | 'warn', message: string) => void;
}

function defaultLog(level: 'info' | 'warn', message: string): void {
  if (level === 'warn') console.warn(message);
  else console.info(message);
}

/**
 * The latch. `observe()` once per sample with the predicate's verdict; `tick()` from a
 * wall-clock timer so the timeout fires even while the frame loop is stalled (a WebGPU
 * pipeline compile holds frames for seconds). Latches ONCE: later samples are ignored.
 */
export class ReadinessSession {
  private readonly startMs: number;
  private readonly timeoutMs: number;
  private readonly holdMs: number;
  private readonly holdFrames: number;
  private readonly log: (level: 'info' | 'warn', message: string) => void;
  private readySince: number | null = null;
  private readyFrames = 0;
  private readonly seen = new Set<string>();
  private lastWaiting: readonly WaitItem[] = [{ key: 'renderer', label: 'the renderer' }];
  private fallbacks: readonly string[] = [];
  private current: ReadinessState;
  private readonly listeners = new Set<(state: ReadinessState) => void>();

  constructor(options: ReadinessSessionOptions) {
    this.startMs = options.startMs;
    this.timeoutMs = options.timeoutMs ?? READINESS_TIMEOUT_MS;
    this.holdMs = options.holdMs ?? READY_HOLD_MS;
    this.holdFrames = options.holdFrames ?? READY_HOLD_FRAMES;
    this.log = options.log ?? defaultLog;
    this.seen.add('renderer');
    this.current = this.snapshot(options.startMs, false, null);
  }

  get state(): ReadinessState {
    return this.current;
  }

  get ready(): boolean {
    return this.current.ready;
  }

  /** Listen for state changes; returns the unsubscribe. */
  subscribe(listener: (state: ReadinessState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Feed one sample. `fallbacks` = permanent fallbacks currently in view (reported only). */
  observe(nowMs: number, verdict: ReadinessVerdict, fallbacks: readonly string[] = []): void {
    if (this.current.ready) return;
    for (const item of verdict.waitingOn) this.seen.add(item.key);
    this.lastWaiting = verdict.waitingOn;
    this.fallbacks = fallbacks;
    if (verdict.ready) {
      this.readySince ??= nowMs;
      this.readyFrames += 1;
      if (nowMs - this.readySince >= this.holdMs && this.readyFrames >= this.holdFrames) {
        this.latch(nowMs, 'ready');
        return;
      }
    } else {
      this.readySince = null;
      this.readyFrames = 0;
    }
    if (!this.tick(nowMs)) this.publish(this.snapshot(nowMs, false, null));
  }

  /** Check the timeout; returns whether it latched. Safe to call from a timer. */
  tick(nowMs: number): boolean {
    if (this.current.ready) return false;
    if (nowMs - this.startMs < this.timeoutMs) return false;
    this.latch(nowMs, 'timeout');
    return true;
  }

  private latch(nowMs: number, reason: ReadinessReason): void {
    const state = this.snapshot(nowMs, true, reason);
    const waited = Math.round(state.elapsedMs);
    if (reason === 'timeout') {
      this.log(
        'warn',
        `[readiness] loading screen lifted on the ${this.timeoutMs} ms timeout after ${waited} ms; ` +
          `still pending: ${state.waitingOn.join(', ') || 'nothing'}`,
      );
    } else {
      this.log('info', `[readiness] in-view assets ready after ${waited} ms`);
    }
    if (state.fallbacks.length > 0) {
      this.log('warn', `[readiness] placeholders on screen: ${state.fallbacks.join(', ')}`);
    }
    this.publish(state);
  }

  private snapshot(nowMs: number, ready: boolean, reason: ReadinessReason | null): ReadinessState {
    const pending = new Set(this.lastWaiting.map((item) => item.key));
    let done = 0;
    for (const key of this.seen) if (!pending.has(key)) done += 1;
    // At a 'ready' latch nothing is pending, so done === total by construction.
    return {
      ready,
      reason,
      elapsedMs: Math.max(0, nowMs - this.startMs),
      readyAtMs: ready ? nowMs : null,
      progress: { done, total: this.seen.size },
      waitingOn: reason === 'ready' ? [] : summarizeLabels(this.lastWaiting),
      fallbacks: this.fallbacks,
    };
  }

  private publish(state: ReadinessState): void {
    this.current = state;
    for (const listener of this.listeners) listener(state);
  }
}

/** Collapse repeated labels: `['creature: x', 'creature: x']` → `['creature: x ×2']`. */
export function summarizeLabels(items: readonly WaitItem[]): string[] {
  const counts = new Map<string, number>();
  for (const item of items) counts.set(item.label, (counts.get(item.label) ?? 0) + 1);
  return [...counts].map(([label, n]) => (n > 1 ? `${label} ×${n}` : label));
}

/** What the page exposes to tooling. */
export interface ReadinessGlobals {
  __tgcReady?: boolean;
  __tgcReadyInfo?: ReadinessState & { revealed: boolean };
}

/** The DOM attribute on the root element: `html[data-tgc-ready="true"]` once revealed. */
export const READY_ATTRIBUTE = 'data-tgc-ready';

/**
 * Publish to tooling: `window.__tgcReady` (true only once the screen is gone — the frame
 * the tool films is the revealed one), `window.__tgcReadyInfo` (the state + `revealed`),
 * and `data-tgc-ready` on the root element. Not dev-gated: it carries no game data.
 */
export function publishReadiness(
  state: ReadinessState,
  revealed: boolean,
  target: ReadinessGlobals,
  root: { setAttribute(name: string, value: string): void } | null,
): void {
  target.__tgcReady = revealed;
  target.__tgcReadyInfo = { ...state, revealed };
  root?.setAttribute(READY_ATTRIBUTE, revealed ? 'true' : 'false');
}
